import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonSpinner, IonTitle } from '@ionic/react';
import { arrowBack, downloadOutline } from 'ionicons/icons';
import React, { useContext, useEffect, useRef, useState } from 'react';
import IonToolbar from '../../../../../components/Modal/ModalToolbar';
import { MessageContext } from '../../../../../contexts/MessageContext';
import { consultarImportacaoCatalogoEmpresa, iniciarImportacaoCatalogoEmpresa } from '../../../../../Utils/Services';
import { CatalogoImportacaoPreview } from '../UploadIA/UploadIA';
import './UploadCatalogoFornecedor.css';

type Props = {
  source: string;
  title: string;
  instructions: string;
  placeholder?: string;
  multiline?: boolean;
  loadingMessage?: string;
  closeAction: Function;
  closeActionSimple: Function;
};

const UploadCatalogoFornecedor: React.FC<Props> = (props) => {
  const { loja } = useContext(MessageContext);
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('');
  const jobKey = `catalogo-importacao:${loja}:${props.source}`;
  const [savedJob, setSavedJob] = useState(() => localStorage.getItem(jobKey) || '');
  const request = useRef(0);
  const pending = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const activeStore = useRef(loja);
  activeStore.current = loja;

  useEffect(() => {
    request.current += 1;
    controller.current?.abort();
    pending.current = false;
    setUrl('');
    setPreview(null);
    setError('');
    setLoading(false);
    setStage('');
    setSavedJob(localStorage.getItem(jobKey) || '');
    return () => { request.current += 1; controller.current?.abort(); };
  }, [loja, props.source, jobKey]);

  const cancel = () => {
    request.current += 1;
    controller.current?.abort();
    pending.current = false;
    props.closeActionSimple();
  };

  const generatePreview = async (resumeJob = '') => {
    if (pending.current) return;
    if (!resumeJob && !url.trim()) { setError('Informe a URL ou o código do catálogo.'); return; }
    const store = loja;
    const version = ++request.current;
    const abort = new AbortController();
    controller.current = abort;
    pending.current = true;
    setLoading(true);
    setError('');
    const isCurrent = () => version === request.current && activeStore.current === store && !abort.signal.aborted;
    try {
      const headers = { token: localStorage.getItem('token') || '', 'Content-Type': 'application/json' };
      let data: any;
      if (resumeJob) data = { jobId: resumeJob, status: 'pendente', source: props.source };
      else {
        const response = await fetch(iniciarImportacaoCatalogoEmpresa.url.replaceAll('{id}', encodeURIComponent(store)), {
          method: iniciarImportacaoCatalogoEmpresa.method, headers,
          body: JSON.stringify({ source: props.source, url: url.trim() }), credentials: 'include', signal: abort.signal,
        });
        data = await response.json();
        if (!response.ok) throw new Error(typeof data?.message === 'string' ? data.message : 'Não foi possível iniciar a importação.');
      }
      if (!isCurrent()) return;
      const started = Date.now();
      if (data.jobId) {
        localStorage.setItem(jobKey, data.jobId); setSavedJob(data.jobId);
        const jobId = data.jobId;
        while (isCurrent() && !['concluido', 'erro'].includes(data.status)) {
          if (Date.now() - started > 12 * 60 * 1000) throw new Error('O processamento está demorando. Você pode retomar a consulta depois.');
          setStage(data.etapa || 'Consultando produtos e complementos...');
          const response = await fetch(consultarImportacaoCatalogoEmpresa.url.replaceAll('{id}', encodeURIComponent(store)).replace('{jobId}', encodeURIComponent(jobId)), {
            method: 'GET', headers, credentials: 'include', signal: abort.signal,
          });
          data = await response.json();
          if (!isCurrent()) return;
          if (!response.ok) {
            if (response.status === 404) { localStorage.removeItem(jobKey); setSavedJob(''); }
            throw new Error(typeof data?.message === 'string' ? data.message : 'Não foi possível consultar a importação.');
          }
          if (!['concluido', 'erro'].includes(data.status)) await new Promise<void>((resolve, reject) => {
            const abortWait = () => { clearTimeout(timer); reject(new Error('Consulta cancelada.')); };
            const timer = setTimeout(() => { abort.signal.removeEventListener('abort', abortWait); resolve(); }, 1500);
            abort.signal.addEventListener('abort', abortWait, { once: true });
          });
        }
        if (data.status === 'erro') { localStorage.removeItem(jobKey); setSavedJob(''); throw new Error(data.mensagem || 'Não foi possível concluir a extração do catálogo.'); }
      }
      if (!isCurrent()) return;
      if (!Array.isArray(data?.preview?.categoriaProdutos) ||
        !Array.isArray(data?.preview?.catalogoProdutos) || data.source !== props.source) {
        throw new Error(typeof data?.message === 'string' ? data.message : 'Não foi possível gerar a prévia do catálogo. Tente novamente.');
      }
      if (!data.preview.catalogoProdutos.length) throw new Error('Nenhum produto encontrado neste catálogo.');
      setPreview({ ...data.preview, resumoImportacao: data.resumo });
    } catch (err: any) {
      if (isCurrent()) setError(err?.message || 'Não foi possível gerar a prévia do catálogo. Tente novamente.');
    } finally {
      if (isCurrent()) { pending.current = false; setLoading(false); }
    }
  };

  if (preview) return <CatalogoImportacaoPreview key={`${loja}-${props.source}`} catalogo={preview}
    source={props.source} titulo={props.title} onBack={() => { setPreview(null); setError(''); }}
    onConfirmed={(attributes: any) => { localStorage.removeItem(jobKey); props.closeAction(attributes); }} />;

  return <>
    <IonHeader className="ion-no-border">
      <IonToolbar className="uploadcatalogo-toolbar">
        <IonButtons slot="start"><IonIcon icon={arrowBack} onClick={cancel} /></IonButtons>
        <IonTitle>{props.title}</IonTitle>
      </IonToolbar>
    </IonHeader>
    <IonContent className="uploadcatalogo-content">
      <section className="uploadcatalogo-intro">
        <span className="uploadcatalogo-icon"><IonIcon icon={downloadOutline} aria-hidden="true" /></span>
        <div><h2>Confira antes de importar</h2>
          <p>Gere uma prévia editável para revisar categorias, produtos, preços e complementos. O catálogo atual será substituído somente ao confirmar a importação.</p>
          {/* <p>O limite para confirmar é de 350.000 bytes. Se o catálogo for maior, remova produtos ou categorias na prévia antes de confirmar.</p>*/}
        </div>
      </section>
      <div className="uploadcatalogo-form">
        <p className="uploadcatalogo-instructions">{props.instructions}</p>
        <label htmlFor="uploadcatalogo-url">URL ou código do catálogo</label>
        <textarea id="uploadcatalogo-url" rows={props.multiline ? 5 : 3} value={url}
          placeholder={props.placeholder} disabled={loading} autoFocus
          aria-describedby={error ? 'uploadcatalogo-error' : undefined} aria-invalid={!!error}
          onChange={event => { setUrl(event.target.value); setError(''); }} />
        {error && <p id="uploadcatalogo-error" role="alert" className="uploadcatalogo-error">{error}</p>}
        {loading && <p role="status" className="uploadcatalogo-status">{stage || props.loadingMessage || 'Buscando os produtos para gerar a prévia...'}</p>}
      </div>
    </IonContent>
    <IonFooter className="ion-no-border uploadcatalogo-footer"><IonToolbar>
      <div className="uploadcatalogo-footer-actions">
        <IonButton fill="outline" className="uploadcatalogo-cancel" onClick={cancel}>Cancelar</IonButton>
        {savedJob && !loading && <IonButton fill="outline" onClick={() => generatePreview(savedJob)}>Retomar importação</IonButton>}
        <IonButton className="uploadcatalogo-primary" disabled={loading} onClick={() => generatePreview()}>
          {loading && <IonSpinner name="crescent" aria-hidden="true" />}{loading ? 'Gerando prévia...' : 'Gerar prévia'}
        </IonButton>
      </div>
    </IonToolbar></IonFooter>
  </>;
};

export default UploadCatalogoFornecedor;
